import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Landmark, 
  Plus, 
  Users, 
  Calendar, 
  AlertTriangle, 
  CheckCircle2, 
  Clock,
  Edit,
  Trash2,
  FileText,
  User
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Tipi di cariche sociali standard
const TIPI_CARICHE = [
  { value: 'presidente', label: 'Presidente' },
  { value: 'vice_presidente', label: 'Vice Presidente' },
  { value: 'segretario', label: 'Segretario' },
  { value: 'tesoriere', label: 'Tesoriere' },
  { value: 'consigliere', label: 'Consigliere' },
  { value: 'revisore', label: 'Revisore dei Conti' },
  { value: 'probiviro', label: 'Probiviro' },
  { value: 'altro', label: 'Altro' }
];

export default function OrganiSociali() {
  const { user } = useAuth();
  const [cariche, setCariche] = useState([]);
  const [persone, setPersone] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingCarica, setEditingCarica] = useState(null);
  
  const [formData, setFormData] = useState({
    tipo_carica: '',
    persona_id: '',
    data_inizio: '',
    data_fine: '',
    note: ''
  });

  useEffect(() => {
    fetchCariche();
    fetchPersone();
  }, []);

  const fetchCariche = async () => {
    try {
      const response = await fetch(`${API_URL}/api/governance/cariche`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setCariche(data.cariche || []);
      }
    } catch (error) {
      console.error('Error fetching cariche:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPersone = async () => {
    try {
      const response = await fetch(`${API_URL}/api/persone`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setPersone(data.persone || data || []);
      }
    } catch (error) {
      console.error('Error fetching persone:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const url = editingCarica 
        ? `${API_URL}/api/governance/cariche/${editingCarica.carica_id}`
        : `${API_URL}/api/governance/cariche`;
      
      const response = await fetch(url, {
        method: editingCarica ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        toast.success(editingCarica ? 'Carica aggiornata' : 'Carica aggiunta');
        setShowAddDialog(false);
        setEditingCarica(null);
        resetForm();
        fetchCariche();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Errore nel salvataggio');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const handleDelete = async (caricaId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa carica?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/governance/cariche/${caricaId}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success('Carica eliminata');
        fetchCariche();
      } else {
        toast.error('Errore nell\'eliminazione');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    }
  };

  const resetForm = () => {
    setFormData({
      tipo_carica: '',
      persona_id: '',
      data_inizio: '',
      data_fine: '',
      note: ''
    });
  };

  const openEditDialog = (carica) => {
    setEditingCarica(carica);
    setFormData({
      tipo_carica: carica.tipo_carica || '',
      persona_id: carica.persona_id || '',
      data_inizio: carica.data_inizio?.split('T')[0] || '',
      data_fine: carica.data_fine?.split('T')[0] || '',
      note: carica.note || ''
    });
    setShowAddDialog(true);
  };

  // Calcola scadenze mandati
  const getScadenzaStatus = (dataFine) => {
    if (!dataFine) return { status: 'indefinito', color: 'bg-slate-100 text-slate-700' };
    
    const oggi = new Date();
    const scadenza = new Date(dataFine);
    const giorniRimanenti = Math.ceil((scadenza - oggi) / (1000 * 60 * 60 * 24));
    
    if (giorniRimanenti < 0) {
      return { status: 'Scaduto', color: 'bg-red-100 text-red-700', icon: AlertTriangle };
    } else if (giorniRimanenti <= 30) {
      return { status: `Scade tra ${giorniRimanenti}g`, color: 'bg-amber-100 text-amber-700', icon: Clock };
    } else if (giorniRimanenti <= 90) {
      return { status: `Scade tra ${giorniRimanenti}g`, color: 'bg-yellow-100 text-yellow-700', icon: Clock };
    }
    return { status: 'Attivo', color: 'bg-green-100 text-green-700', icon: CheckCircle2 };
  };

  // Raggruppa cariche per tipo
  const caricheAttive = cariche.filter(c => {
    if (!c.data_fine) return true;
    return new Date(c.data_fine) >= new Date();
  });

  const caricheStoriche = cariche.filter(c => {
    if (!c.data_fine) return false;
    return new Date(c.data_fine) < new Date();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="organi-sociali-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Landmark className="w-7 h-7 text-indigo-600" />
            Organi Sociali
          </h1>
          <p className="text-slate-600 mt-1">
            Gestisci le cariche sociali e i mandati dell'associazione
          </p>
        </div>
        <Button onClick={() => { resetForm(); setEditingCarica(null); setShowAddDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Nuova Carica
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-indigo-100 rounded-lg">
                <Users className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{caricheAttive.length}</p>
                <p className="text-sm text-slate-500">Cariche Attive</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {caricheAttive.filter(c => {
                    const status = getScadenzaStatus(c.data_fine);
                    return status.status.includes('Scade');
                  }).length}
                </p>
                <p className="text-sm text-slate-500">In Scadenza</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <AlertTriangle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {cariche.filter(c => {
                    const status = getScadenzaStatus(c.data_fine);
                    return status.status === 'Scaduto';
                  }).length}
                </p>
                <p className="text-sm text-slate-500">Scadute</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <FileText className="w-5 h-5 text-slate-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{caricheStoriche.length}</p>
                <p className="text-sm text-slate-500">Storico</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="attive" className="space-y-4">
        <TabsList>
          <TabsTrigger value="attive">Cariche Attive ({caricheAttive.length})</TabsTrigger>
          <TabsTrigger value="storico">Storico ({caricheStoriche.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="attive">
          {caricheAttive.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Landmark className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Nessuna carica attiva</h3>
                <p className="text-slate-500 mb-4">Inizia aggiungendo le cariche sociali dell'associazione</p>
                <Button onClick={() => setShowAddDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi Prima Carica
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {caricheAttive.map((carica) => {
                const scadenza = getScadenzaStatus(carica.data_fine);
                const ScadenzaIcon = scadenza.icon || CheckCircle2;
                
                return (
                  <Card key={carica.carica_id} className="hover:shadow-md transition-shadow">
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-lg">
                            {TIPI_CARICHE.find(t => t.value === carica.tipo_carica)?.label || carica.tipo_carica}
                          </CardTitle>
                          <CardDescription className="flex items-center gap-1 mt-1">
                            <User className="w-3 h-3" />
                            {carica.persona_nome || 'Non assegnato'}
                          </CardDescription>
                        </div>
                        <Badge className={scadenza.color}>
                          <ScadenzaIcon className="w-3 h-3 mr-1" />
                          {scadenza.status}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2 text-sm text-slate-600">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4" />
                          <span>
                            Dal {carica.data_inizio ? new Date(carica.data_inizio).toLocaleDateString('it-IT') : 'N/D'}
                            {carica.data_fine && ` al ${new Date(carica.data_fine).toLocaleDateString('it-IT')}`}
                          </span>
                        </div>
                        {carica.note && (
                          <p className="text-slate-500 italic">{carica.note}</p>
                        )}
                      </div>
                      <div className="flex gap-2 mt-4">
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(carica)}>
                          <Edit className="w-3 h-3 mr-1" />
                          Modifica
                        </Button>
                        <Button variant="outline" size="sm" className="text-red-600 hover:text-red-700" onClick={() => handleDelete(carica.carica_id)}>
                          <Trash2 className="w-3 h-3 mr-1" />
                          Elimina
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="storico">
          {caricheStoriche.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900">Nessuna carica storica</h3>
                <p className="text-slate-500">Le cariche terminate appariranno qui</p>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="pt-6">
                <div className="space-y-3">
                  {caricheStoriche.map((carica) => (
                    <div key={carica.carica_id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium">
                          {TIPI_CARICHE.find(t => t.value === carica.tipo_carica)?.label || carica.tipo_carica}
                        </p>
                        <p className="text-sm text-slate-500">
                          {carica.persona_nome} • {new Date(carica.data_inizio).toLocaleDateString('it-IT')} - {new Date(carica.data_fine).toLocaleDateString('it-IT')}
                        </p>
                      </div>
                      <Badge variant="outline">Terminata</Badge>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCarica ? 'Modifica Carica' : 'Nuova Carica Sociale'}</DialogTitle>
            <DialogDescription>
              {editingCarica ? 'Modifica i dettagli della carica' : 'Aggiungi una nuova carica sociale all\'organigramma'}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Tipo di Carica *</Label>
              <Select value={formData.tipo_carica} onValueChange={(v) => setFormData({...formData, tipo_carica: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona tipo carica" />
                </SelectTrigger>
                <SelectContent>
                  {TIPI_CARICHE.map((tipo) => (
                    <SelectItem key={tipo.value} value={tipo.value}>{tipo.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Persona *</Label>
              <Select value={formData.persona_id} onValueChange={(v) => setFormData({...formData, persona_id: v})}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona persona" />
                </SelectTrigger>
                <SelectContent>
                  {persone.map((persona) => (
                    <SelectItem key={persona.persona_id} value={persona.persona_id}>
                      {persona.nome} {persona.cognome}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Data Inizio *</Label>
                <Input 
                  type="date" 
                  value={formData.data_inizio}
                  onChange={(e) => setFormData({...formData, data_inizio: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Data Fine (opzionale)</Label>
                <Input 
                  type="date" 
                  value={formData.data_fine}
                  onChange={(e) => setFormData({...formData, data_fine: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Note</Label>
              <Input 
                value={formData.note}
                onChange={(e) => setFormData({...formData, note: e.target.value})}
                placeholder="Note aggiuntive..."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddDialog(false)}>
                Annulla
              </Button>
              <Button type="submit">
                {editingCarica ? 'Salva Modifiche' : 'Aggiungi Carica'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
